/**
 * SHEIN-Inspired Fashion E-Commerce Theme
 * Main JavaScript - Vanilla JS, no dependencies
 */
(function () {
  'use strict';

  /* ==========================================================================
     Utility Helpers
     ========================================================================== */

  const Utils = {
    /**
     * Debounce function execution
     */
    debounce(fn, delay = 300) {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    },

    /**
     * Fetch wrapper for Shopify AJAX API
     */
    async fetchJSON(url, options = {}) {
      const defaults = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      };
      const config = { ...defaults, ...options };
      if (config.body && typeof config.body === 'object') {
        config.body = JSON.stringify(config.body);
      }
      const response = await fetch(url, config);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || response.statusText);
      }
      return response.json();
    },

    /**
     * Parse a DOM element from an HTML string
     */
    parseHTML(html) {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      return template.content;
    },

    /**
     * Format money in cents to currency string (basic)
     * In production, override with Shopify.formatMoney if available.
     */
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      if (typeof Shopify !== 'undefined' && Shopify.money_format) {
        return Shopify.money_format.replace(/\{\{\s*amount\s*\}\}/, amount)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
      }
      return '$' + amount;
    },

    /**
     * Lock body scroll
     */
    lockScroll() {
      document.body.style.overflow = 'hidden';
      document.body.dataset.scrollLocked = 'true';
    },

    /**
     * Unlock body scroll
     */
    unlockScroll() {
      document.body.style.overflow = '';
      delete document.body.dataset.scrollLocked;
    },

    /**
     * Get a cookie value by name
     */
    getCookie(name) {
      const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? decodeURIComponent(match[2]) : null;
    },

    /**
     * Set a cookie
     */
    setCookie(name, value, days = 30) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + date.toUTCString() + ';path=/';
    },
  };

  /* ==========================================================================
     Toast Notifications
     ========================================================================== */

  const Toast = {
    _container: null,

    _getContainer() {
      if (!this._container) {
        this._container = document.createElement('div');
        this._container.className = 'toast-container';
        this._container.setAttribute('aria-live', 'polite');
        document.body.appendChild(this._container);
      }
      return this._container;
    },

    /**
     * Show a toast notification
     * @param {string} message - The message to display
     * @param {string} type - 'success' or 'error'
     * @param {number} duration - Duration in ms (default 3000)
     */
    show(message, type = 'success', duration = 3000) {
      const container = this._getContainer();
      const toast = document.createElement('div');
      toast.className = 'toast toast--' + type;
      toast.setAttribute('role', 'status');
      toast.innerHTML =
        '<span class="toast__message">' + message + '</span>' +
        '<button class="toast__close" aria-label="Close">&times;</button>';

      container.appendChild(toast);

      // Trigger reflow then add visible class for animation
      toast.offsetHeight; // force reflow
      toast.classList.add('toast--visible');

      const closeBtn = toast.querySelector('.toast__close');
      let dismissTimer;

      const dismiss = () => {
        clearTimeout(dismissTimer);
        toast.classList.remove('toast--visible');
        toast.addEventListener('transitionend', () => toast.remove(), { once: true });
        // Fallback removal
        setTimeout(() => {
          if (toast.parentNode) toast.remove();
        }, 500);
      };

      closeBtn.addEventListener('click', dismiss);
      dismissTimer = setTimeout(dismiss, duration);
    },

    success(message, duration) {
      this.show(message, 'success', duration);
    },

    error(message, duration) {
      this.show(message, 'error', duration);
    },
  };

  /* ==========================================================================
     Cart API
     ========================================================================== */

  /* Locale-aware root path (e.g. "/ar/" or "/fr/" or "/") */
  const rootUrl = (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) || '/';

  const CartAPI = {
    /**
     * Get current cart state
     */
    async get() {
      return Utils.fetchJSON(rootUrl + 'cart.js');
    },

    /**
     * Add item to cart
     * @param {number|string} variantId
     * @param {number} quantity
     * @param {object} properties - Optional line item properties
     */
    async add(variantId, quantity = 1, properties = {}) {
      const body = {
        items: [{
          id: parseInt(variantId, 10),
          quantity: quantity,
        }],
      };
      if (Object.keys(properties).length > 0) {
        body.items[0].properties = properties;
      }
      return Utils.fetchJSON(rootUrl + 'cart/add.js', { method: 'POST', body });
    },

    /**
     * Change item quantity in cart
     * @param {string} lineKey - Line item key
     * @param {number} quantity
     */
    async change(lineKey, quantity) {
      return Utils.fetchJSON(rootUrl + 'cart/change.js', {
        method: 'POST',
        body: { id: lineKey, quantity },
      });
    },

    /**
     * Remove item from cart (set quantity to 0)
     */
    async remove(lineKey) {
      return this.change(lineKey, 0);
    },

    /**
     * Update cart note
     */
    async updateNote(note) {
      return Utils.fetchJSON(rootUrl + 'cart/update.js', {
        method: 'POST',
        body: { note },
      });
    },
  };

  /* ==========================================================================
     Hero Carousel
     ========================================================================== */

  class HeroCarousel {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('.hero-carousel__track');
      this.slides = el.querySelectorAll('.hero-carousel__slide');
      this.prevBtn = el.querySelector('.hero-carousel__prev');
      this.nextBtn = el.querySelector('.hero-carousel__next');
      this.dotsContainer = el.querySelector('.hero-carousel__dots');
      this.currentIndex = 0;
      this.slideCount = this.slides.length;
      this.interval = parseInt(el.dataset.interval, 10) || 5000;
      this.autoplayTimer = null;
      this.isHovered = false;
      this.touchStartX = 0;
      this.touchEndX = 0;

      if (this.slideCount <= 1) return;

      this._buildDots();
      this._bindEvents();
      this._startAutoplay();
      this._goTo(0);
    }

    _buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      for (let i = 0; i < this.slideCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'hero-carousel__dot' + (i === 0 ? ' hero-carousel__dot--active' : '');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.dataset.index = i;
        this.dotsContainer.appendChild(dot);
      }
      this.dots = this.dotsContainer.querySelectorAll('.hero-carousel__dot');
    }

    _bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.prev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.next());
      }
      if (this.dotsContainer) {
        this.dotsContainer.addEventListener('click', (e) => {
          const dot = e.target.closest('.hero-carousel__dot');
          if (dot) {
            this._goTo(parseInt(dot.dataset.index, 10));
          }
        });
      }

      // Pause on hover
      this.el.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this._stopAutoplay();
      });
      this.el.addEventListener('mouseleave', () => {
        this.isHovered = false;
        this._startAutoplay();
      });

      // Touch / swipe support
      this.el.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.el.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this._handleSwipe();
      }, { passive: true });
    }

    _handleSwipe() {
      const diff = this.touchStartX - this.touchEndX;
      const threshold = 50;
      if (Math.abs(diff) < threshold) return;
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    _goTo(index) {
      // Infinite looping
      if (index < 0) index = this.slideCount - 1;
      if (index >= this.slideCount) index = 0;

      this.currentIndex = index;

      if (this.track) {
        this.track.style.transform = 'translateX(-' + (index * 100) + '%)';
      }

      // Update slide visibility
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('hero-carousel__slide--active', i === index);
        slide.setAttribute('aria-hidden', i !== index);
      });

      // Update dots
      if (this.dots) {
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('hero-carousel__dot--active', i === index);
        });
      }
    }

    prev() {
      this._goTo(this.currentIndex - 1);
      this._resetAutoplay();
    }

    next() {
      this._goTo(this.currentIndex + 1);
      this._resetAutoplay();
    }

    _startAutoplay() {
      if (this.autoplayTimer || this.isHovered) return;
      this.autoplayTimer = setInterval(() => this.next(), this.interval);
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    _resetAutoplay() {
      this._stopAutoplay();
      if (!this.isHovered) {
        this._startAutoplay();
      }
    }

    destroy() {
      this._stopAutoplay();
    }
  }

  /* ==========================================================================
     Mobile Menu
     ========================================================================== */

  class MobileMenu {
    constructor() {
      this.menu = document.querySelector('.mobile-menu');
      this.overlay = document.querySelector('.mobile-menu__overlay');
      this.toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
      this.closeBtn = document.querySelector('.mobile-menu__close');

      if (!this.menu) return;

      this._bindEvents();
    }

    _bindEvents() {
      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => this.open());
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Accordion category expansion
      this.menu.addEventListener('click', (e) => {
        const trigger = e.target.closest('.mobile-menu__accordion-trigger');
        if (!trigger) return;

        e.preventDefault();
        const item = trigger.closest('.mobile-menu__item');
        if (!item) return;

        const submenu = item.querySelector('.mobile-menu__submenu');
        if (!submenu) return;

        const isOpen = item.classList.contains('mobile-menu__item--open');

        // Close other open items at the same level
        const siblings = item.parentNode.querySelectorAll('.mobile-menu__item--open');
        siblings.forEach((sibling) => {
          if (sibling !== item) {
            sibling.classList.remove('mobile-menu__item--open');
            const sub = sibling.querySelector('.mobile-menu__submenu');
            if (sub) sub.style.maxHeight = null;
          }
        });

        // Toggle current
        item.classList.toggle('mobile-menu__item--open', !isOpen);
        if (!isOpen) {
          submenu.style.maxHeight = submenu.scrollHeight + 'px';
        } else {
          submenu.style.maxHeight = null;
        }
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    open() {
      this.menu.classList.add('mobile-menu--open');
      if (this.overlay) this.overlay.classList.add('mobile-menu__overlay--visible');
      Utils.lockScroll();
      this.menu.setAttribute('aria-hidden', 'false');
      // Focus first focusable element
      const firstFocusable = this.menu.querySelector('a, button, input');
      if (firstFocusable) firstFocusable.focus();
    }

    close() {
      this.menu.classList.remove('mobile-menu--open');
      if (this.overlay) this.overlay.classList.remove('mobile-menu__overlay--visible');
      Utils.unlockScroll();
      this.menu.setAttribute('aria-hidden', 'true');
      if (this.toggleBtn) this.toggleBtn.focus();
    }

    isOpen() {
      return this.menu.classList.contains('mobile-menu--open');
    }
  }

  /* ==========================================================================
     Cart Drawer
     ========================================================================== */

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('.cart-drawer');
      this.overlay = document.querySelector('.cart-drawer__overlay');
      this.closeBtn = document.querySelector('.cart-drawer__close');
      this.cartToggle = document.querySelectorAll('[data-cart-toggle]');
      this.cartCount = document.querySelectorAll('[data-cart-count]');
      this.cartBody = document.querySelector('.cart-drawer__body');

      if (!this.drawer) return;

      this._bindEvents();
    }

    _bindEvents() {
      // Open cart drawer
      this.cartToggle.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close cart drawer
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      /* Debounce timers per line-key and request versioning */
      this._qtyTimers = {};
      this._pendingQty = {};
      this._requestId = 0;

      // Event delegation for cart drawer actions
      if (this.drawer) {
        this.drawer.addEventListener('click', (e) => {
          // Quantity buttons
          const qtyBtn = e.target.closest('[data-cart-qty]');
          if (qtyBtn) {
            e.preventDefault();
            const lineKey = qtyBtn.dataset.lineKey;
            const action = qtyBtn.dataset.cartQty;
            const qtyContainer = qtyBtn.closest('.cart-item__qty') || qtyBtn.closest('.cart-drawer__qty');
            const qtyDisplay = qtyContainer ? (qtyContainer.querySelector('span') || qtyContainer.querySelector('.cart-drawer__qty-input')) : null;
            let qty = qtyDisplay ? (parseInt(qtyDisplay.textContent || qtyDisplay.value, 10) || 1) : 1;

            if (action === 'minus') {
              qty = Math.max(0, qty - 1);
            } else if (action === 'plus') {
              qty = qty + 1;
            }

            /* Optimistic UI: update quantity display instantly */
            if (qty === 0) {
              const cartItem = qtyBtn.closest('.cart-item');
              if (cartItem) {
                cartItem.style.opacity = '0.4';
                cartItem.style.pointerEvents = 'none';
              }
              /* Remove immediately, no debounce */
              this._updateItem(lineKey, 0);
            } else {
              if (qtyDisplay) {
                if (qtyDisplay.tagName === 'SPAN') qtyDisplay.textContent = qty;
                else qtyDisplay.value = qty;
              }
              /* Debounce: wait 400ms after last click before sending API call */
              this._pendingQty[lineKey] = qty;
              clearTimeout(this._qtyTimers[lineKey]);
              this._qtyTimers[lineKey] = setTimeout(() => {
                this._updateItem(lineKey, this._pendingQty[lineKey]);
                delete this._pendingQty[lineKey];
              }, 400);
            }
            return;
          }

          // Remove item
          const removeBtn = e.target.closest('[data-cart-remove]');
          if (removeBtn) {
            e.preventDefault();
            const lineKey = removeBtn.dataset.lineKey;
            /* Cancel any pending qty update for this item */
            clearTimeout(this._qtyTimers[lineKey]);
            delete this._pendingQty[lineKey];
            /* Optimistic UI: fade out item instantly */
            const cartItem = removeBtn.closest('.cart-item');
            if (cartItem) {
              cartItem.style.transition = 'opacity 0.2s';
              cartItem.style.opacity = '0';
              cartItem.style.pointerEvents = 'none';
            }
            this._updateItem(lineKey, 0);
            return;
          }
        });
      }

      // Intercept product forms for AJAX add-to-cart
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action="/cart/add"]');
        if (!form) return;

        e.preventDefault();
        this._handleFormSubmit(form);
      });
    }

    async _handleFormSubmit(form) {
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      const myId = ++this._requestId;

      try {
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;

        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propName = key.replace('properties[', '').replace(']', '');
            properties[propName] = value;
          }
        }

        this._bumpBadge(quantity);

        await CartAPI.add(variantId, quantity, properties);
        if (myId === this._requestId) {
          Toast.success((window.__cartStrings && window.__cartStrings.addedToCart) || 'Added to cart!');
          const cart = await CartAPI.get();
          if (myId === this._requestId) {
            this._renderCart(cart);
            this._updateCartCount(cart.item_count);
          }
        }
      } catch (err) {
        if (myId === this._requestId) {
          Toast.error(err.message || 'Could not add to cart');
          this.refresh();
        }
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    }

    async _updateItem(lineKey, quantity) {
      const myId = ++this._requestId;
      try {
        const cart = await CartAPI.change(lineKey, quantity);
        /* Only render if this is still the latest request */
        if (myId === this._requestId) {
          this._renderCart(cart);
          this._updateCartCount(cart.item_count);
        }
      } catch (err) {
        if (myId === this._requestId) {
          Toast.error(err.message || 'Could not update cart');
          this.refresh();
        }
      }
    }

    /* Optimistically bump badge by delta */
    _bumpBadge(delta) {
      var badge = document.getElementById('cart-count');
      if (badge) {
        var current = parseInt(badge.textContent, 10) || 0;
        var next = Math.max(0, current + delta);
        badge.textContent = next;
        badge.style.display = next > 0 ? 'flex' : 'none';
      }
      var titleEl = document.querySelector('.cart-drawer__title');
      if (titleEl) {
        var m = titleEl.textContent.match(/\((\d+)\)/);
        var cur = m ? parseInt(m[1], 10) : 0;
        var label = (window.__cartStrings && window.__cartStrings.shoppingBag) || 'Shopping Bag';
        titleEl.textContent = label + ' (' + Math.max(0, cur + delta) + ')';
      }
    }

    async refresh() {
      const myId = ++this._requestId;
      try {
        const cart = await CartAPI.get();
        if (myId === this._requestId) {
          this._renderCart(cart);
          this._updateCartCount(cart.item_count);
        }
      } catch (err) {
        console.error('Failed to refresh cart:', err);
      }
    }

    _renderCart(cart) {
      if (!this.cartBody) return;

      /* Update drawer title */
      var titleEl = document.querySelector('.cart-drawer__title');
      var bagLabel = (window.__cartStrings && window.__cartStrings.shoppingBag) || 'Shopping Bag';
      if (titleEl) titleEl.textContent = bagLabel + ' (' + cart.item_count + ')';

      /* Update header badge */
      var badge = document.getElementById('cart-count');
      if (badge) {
        badge.textContent = cart.item_count;
        badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }

      if (!cart.items || cart.items.length === 0) {
        this.cartBody.innerHTML =
          '<div class="cart-drawer__empty">' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="width:64px;height:64px;margin-bottom:16px;color:#ccc;"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' +
            '<p>' + ((window.__cartStrings && window.__cartStrings.bagEmpty) || 'Your shopping bag is empty') + '</p>' +
            '<a href="/collections" style="display:inline-block;background:#000;color:#fff;padding:12px 32px;font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;text-decoration:none;">' + ((window.__cartStrings && window.__cartStrings.shopNow) || 'Shop Now') + '</a>' +
          '</div>';
        /* Hide footer */
        var footer = document.querySelector('.cart-drawer__footer');
        if (footer) footer.style.display = 'none';
        this._updateSubtotal(0);
        return;
      }

      /* Show footer */
      var footer = document.querySelector('.cart-drawer__footer');
      if (footer) footer.style.display = '';

      let html = '';
      cart.items.forEach((item, i) => {
        const imageUrl = item.featured_image
          ? item.featured_image.url + '&width=200'
          : (item.image ? item.image + '&width=200' : '');
        const variantHtml = item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="cart-item__variant">' + item.variant_title + '</p>'
          : '';
        const escapedTitle = (item.product_title || '').replace(/"/g, '&quot;');

        html +=
          '<div class="cart-item" data-line-key="' + item.key + '">' +
            '<div class="cart-item__image">' +
              (imageUrl
                ? '<img src="' + imageUrl + '" alt="' + escapedTitle + '" loading="lazy" width="90" height="120">'
                : '') +
            '</div>' +
            '<div class="cart-item__details">' +
              '<p class="cart-item__title"><a href="' + item.url + '">' + item.product_title + '</a></p>' +
              variantHtml +
              '<p class="cart-item__price">' + Utils.formatMoney(item.final_line_price) + '</p>' +
              '<div class="cart-item__bottom">' +
                '<div class="cart-item__qty">' +
                  '<button data-cart-qty="minus" data-line-key="' + item.key + '" aria-label="' + ((window.__cartStrings && window.__cartStrings.decreaseQty) || 'Decrease quantity') + '">\u2212</button>' +
                  '<span>' + item.quantity + '</span>' +
                  '<button data-cart-qty="plus" data-line-key="' + item.key + '" aria-label="' + ((window.__cartStrings && window.__cartStrings.increaseQty) || 'Increase quantity') + '">+</button>' +
                '</div>' +
              '</div>' +
            '</div>' +
            '<button class="cart-item__remove" data-cart-remove data-line-key="' + item.key + '" aria-label="' + ((window.__cartStrings && window.__cartStrings.remove) || 'Remove') + ' ' + escapedTitle + '">' +
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>' +
            '</button>' +
          '</div>';
      });
      this.cartBody.innerHTML = html;

      this._updateSubtotal(cart.total_price);
    }

    _updateSubtotal(totalPrice) {
      const subtotalEls = document.querySelectorAll('[data-cart-subtotal]');
      subtotalEls.forEach((el) => {
        el.textContent = Utils.formatMoney(totalPrice);
      });
    }

    _updateCartCount(count) {
      this.cartCount.forEach((el) => {
        el.textContent = count;
        el.classList.toggle('cart-count--hidden', count === 0);
      });
    }

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('cart-drawer--open');
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('cart-drawer__overlay--visible');
      var headerOverlay = document.getElementById('header-overlay');
      if (headerOverlay) headerOverlay.classList.add('is-active');
      Utils.lockScroll();
      this.drawer.setAttribute('aria-hidden', 'false');
    }

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('cart-drawer--open');
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('cart-drawer__overlay--visible');
      var headerOverlay = document.getElementById('header-overlay');
      if (headerOverlay) headerOverlay.classList.remove('is-active');
      Utils.unlockScroll();
      this.drawer.setAttribute('aria-hidden', 'true');
    }

    isOpen() {
      return this.drawer && this.drawer.classList.contains('cart-drawer--open');
    }
  }

  /* ==========================================================================
     Mega Menu (Desktop)
     ========================================================================== */

  class MegaMenu {
    constructor() {
      this.triggers = document.querySelectorAll('[data-mega-menu-trigger]');
      this.showDelay = 150;
      this.hideDelay = 200;

      if (!this.triggers.length) return;

      this._bindEvents();
    }

    _bindEvents() {
      this.triggers.forEach((trigger) => {
        let showTimer = null;
        let hideTimer = null;
        const menuId = trigger.dataset.megaMenuTrigger;
        const megaPanel = document.querySelector('[data-mega-menu="' + menuId + '"]');
        if (!megaPanel) return;

        const show = () => {
          clearTimeout(hideTimer);
          showTimer = setTimeout(() => {
            // Hide all other mega menus first
            document.querySelectorAll('.mega-menu--visible').forEach((m) => {
              if (m !== megaPanel) m.classList.remove('mega-menu--visible');
            });
            document.querySelectorAll('[data-mega-menu-trigger].mega-menu-trigger--active').forEach((t) => {
              if (t !== trigger) t.classList.remove('mega-menu-trigger--active');
            });

            megaPanel.classList.add('mega-menu--visible');
            trigger.classList.add('mega-menu-trigger--active');
          }, this.showDelay);
        };

        const hide = () => {
          clearTimeout(showTimer);
          hideTimer = setTimeout(() => {
            megaPanel.classList.remove('mega-menu--visible');
            trigger.classList.remove('mega-menu-trigger--active');
          }, this.hideDelay);
        };

        trigger.addEventListener('mouseenter', show);
        trigger.addEventListener('mouseleave', hide);
        megaPanel.addEventListener('mouseenter', () => {
          clearTimeout(hideTimer);
        });
        megaPanel.addEventListener('mouseleave', hide);

        // Keyboard accessibility
        trigger.addEventListener('focus', show);
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            hide();
            trigger.focus();
          }
        });
      });
    }
  }

  /* ==========================================================================
     Flash Sale Countdown
     ========================================================================== */

  class FlashSaleCountdown {
    constructor(el) {
      this.el = el;
      this.endTime = new Date(el.dataset.endTime).getTime();
      this.hoursEl = el.querySelector('[data-countdown-hours]');
      this.minutesEl = el.querySelector('[data-countdown-minutes]');
      this.secondsEl = el.querySelector('[data-countdown-seconds]');
      this.timer = null;

      if (isNaN(this.endTime)) return;

      this._tick();
      this.timer = setInterval(() => this._tick(), 1000);
    }

    _tick() {
      const now = Date.now();
      const diff = this.endTime - now;

      if (diff <= 0) {
        clearInterval(this.timer);
        this.el.classList.add('countdown--ended');
        if (this.hoursEl) this.hoursEl.textContent = '00';
        if (this.minutesEl) this.minutesEl.textContent = '00';
        if (this.secondsEl) this.secondsEl.textContent = '00';

        // Optionally show ENDED text
        const endedEl = this.el.querySelector('[data-countdown-ended]');
        if (endedEl) {
          endedEl.style.display = '';
          endedEl.textContent = 'ENDED';
        }
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
      if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
      if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
    }

    destroy() {
      if (this.timer) clearInterval(this.timer);
    }
  }

  /* ==========================================================================
     Quick Add to Cart
     ========================================================================== */

  class QuickAdd {
    constructor(cartDrawer) {
      this.cartDrawer = cartDrawer;
      this._bindEvents();
    }

    _bindEvents() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.product-card__quick-add');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();
        this._addToCart(btn);
      });
    }

    async _addToCart(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      btn.classList.add('product-card__quick-add--loading');

      if (this.cartDrawer) this.cartDrawer._bumpBadge(1);

      try {
        await CartAPI.add(variantId, 1);
        Toast.success((window.__cartStrings && window.__cartStrings.addedToCart) || 'Added to cart!');
        if (this.cartDrawer) {
          /* Use versioned refresh + open only if still latest */
          const myId = ++this.cartDrawer._requestId;
          const cart = await CartAPI.get();
          if (myId === this.cartDrawer._requestId) {
            this.cartDrawer._renderCart(cart);
            this.cartDrawer._updateCartCount(cart.item_count);
          }
        }
      } catch (err) {
        Toast.error(err.message || 'Could not add to cart');
        if (this.cartDrawer) this.cartDrawer.refresh();
      } finally {
        btn.disabled = false;
        btn.classList.remove('product-card__quick-add--loading');
      }
    }
  }

  /* ==========================================================================
     Product Page
     ========================================================================== */

  class ProductPage {
    constructor() {
      this.container = document.querySelector('[data-product-page]');
      if (!this.container) return;

      this.productJSON = null;
      this.currentVariant = null;

      this._loadProductData();
      this._bindVariantSelection();
      this._bindImageGallery();
      this._bindAccordion();
      this._bindQuantity();
    }

    _loadProductData() {
      const dataEl = this.container.querySelector('[data-product-json]');
      if (dataEl) {
        try {
          this.productJSON = JSON.parse(dataEl.textContent);
          // Set initial variant
          const initialVariantId = this.container.querySelector('[name="id"]');
          if (initialVariantId && this.productJSON.variants) {
            this.currentVariant = this.productJSON.variants.find(
              (v) => v.id === parseInt(initialVariantId.value, 10)
            );
          }
        } catch (e) {
          console.error('Failed to parse product JSON:', e);
        }
      }
    }

    _bindVariantSelection() {
      // Size buttons
      this.container.addEventListener('click', (e) => {
        const sizeBtn = e.target.closest('.product-option__size-btn');
        if (!sizeBtn) return;

        e.preventDefault();
        const optionGroup = sizeBtn.closest('.product-option');
        if (optionGroup) {
          optionGroup.querySelectorAll('.product-option__size-btn').forEach((b) => {
            b.classList.remove('product-option__size-btn--active');
          });
        }
        sizeBtn.classList.add('product-option__size-btn--active');
        this._onOptionChange();
      });

      // Color swatches
      this.container.addEventListener('click', (e) => {
        const swatch = e.target.closest('.product-option__color-swatch');
        if (!swatch) return;

        e.preventDefault();
        const optionGroup = swatch.closest('.product-option');
        if (optionGroup) {
          optionGroup.querySelectorAll('.product-option__color-swatch').forEach((s) => {
            s.classList.remove('product-option__color-swatch--active');
          });
        }
        swatch.classList.add('product-option__color-swatch--active');
        this._onOptionChange();
      });

      // Standard select dropdowns
      this.container.querySelectorAll('.product-option__select').forEach((select) => {
        select.addEventListener('change', () => this._onOptionChange());
      });
    }

    _onOptionChange() {
      if (!this.productJSON || !this.productJSON.variants) return;

      // Gather selected options
      const selectedOptions = [];
      this.container.querySelectorAll('.product-option').forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);

        // Check for active button
        const activeBtn = group.querySelector('.product-option__size-btn--active, .product-option__color-swatch--active');
        if (activeBtn) {
          selectedOptions[index] = activeBtn.dataset.value;
          return;
        }

        // Check for select
        const select = group.querySelector('.product-option__select');
        if (select) {
          selectedOptions[index] = select.value;
        }
      });

      // Find matching variant
      const variant = this.productJSON.variants.find((v) => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });

      if (variant) {
        this.currentVariant = variant;
        this._updateVariantDisplay(variant);
      }
    }

    _updateVariantDisplay(variant) {
      // Update hidden variant ID input
      const idInput = this.container.querySelector('[name="id"]');
      if (idInput) idInput.value = variant.id;

      // Update price
      const priceEl = this.container.querySelector('[data-product-price]');
      if (priceEl) priceEl.textContent = Utils.formatMoney(variant.price);

      const comparePriceEl = this.container.querySelector('[data-product-compare-price]');
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = Utils.formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      // Update discount badge
      const discountEl = this.container.querySelector('[data-product-discount]');
      if (discountEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const percent = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          discountEl.textContent = '-' + percent + '%';
          discountEl.style.display = '';
        } else {
          discountEl.style.display = 'none';
        }
      }

      // Update add to cart button
      const addBtn = this.container.querySelector('[data-add-to-cart]');
      const addBtnText = this.container.querySelector('[data-add-to-cart-text]');
      if (addBtn && addBtnText) {
        if (variant.available) {
          addBtn.disabled = false;
          addBtnText.textContent = 'Add to Cart';
        } else {
          addBtn.disabled = true;
          addBtnText.textContent = 'Sold Out';
        }
      }

      // Update variant image
      if (variant.featured_image) {
        const mainImage = this.container.querySelector('.product-gallery__main-image');
        if (mainImage) {
          mainImage.src = variant.featured_image.src;
          mainImage.alt = variant.featured_image.alt || '';
        }
      }

      // Update URL without reload
      if (variant.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    _bindImageGallery() {
      const gallery = this.container.querySelector('.product-gallery');
      if (!gallery) return;

      gallery.addEventListener('click', (e) => {
        const thumb = e.target.closest('.product-gallery__thumbnail');
        if (!thumb) return;

        e.preventDefault();
        const mainImage = gallery.querySelector('.product-gallery__main-image');
        if (mainImage) {
          mainImage.src = thumb.dataset.src || thumb.src;
          mainImage.alt = thumb.alt || '';
        }

        // Update active state
        gallery.querySelectorAll('.product-gallery__thumbnail').forEach((t) => {
          t.classList.remove('product-gallery__thumbnail--active');
        });
        thumb.classList.add('product-gallery__thumbnail--active');
      });
    }

    _bindAccordion() {
      this.container.addEventListener('click', (e) => {
        const trigger = e.target.closest('.product-accordion__trigger');
        if (!trigger) return;

        e.preventDefault();
        const item = trigger.closest('.product-accordion__item');
        if (!item) return;

        const content = item.querySelector('.product-accordion__content');
        if (!content) return;

        const isOpen = item.classList.contains('product-accordion__item--open');
        item.classList.toggle('product-accordion__item--open', !isOpen);

        if (!isOpen) {
          content.style.maxHeight = content.scrollHeight + 'px';
        } else {
          content.style.maxHeight = null;
        }
      });
    }

    _bindQuantity() {
      this.container.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-btn]');
        if (!btn) return;

        e.preventDefault();
        const input = this.container.querySelector('[data-qty-input]');
        if (!input) return;

        let qty = parseInt(input.value, 10) || 1;
        const action = btn.dataset.qtyBtn;

        if (action === 'minus') {
          qty = Math.max(1, qty - 1);
        } else if (action === 'plus') {
          qty = qty + 1;
        }

        input.value = qty;
      });
    }
  }

  /* ==========================================================================
     Collection Page - Filters & Sort
     ========================================================================== */

  class CollectionPage {
    constructor() {
      this.container = document.querySelector('[data-collection-page]');
      if (!this.container) return;

      this._bindFilterToggles();
      this._bindSort();
    }

    _bindFilterToggles() {
      this.container.addEventListener('click', (e) => {
        const trigger = e.target.closest('.filter-group__trigger');
        if (!trigger) return;

        e.preventDefault();
        const group = trigger.closest('.filter-group');
        if (!group) return;

        group.classList.toggle('filter-group--open');
        const content = group.querySelector('.filter-group__content');
        if (content) {
          if (group.classList.contains('filter-group--open')) {
            content.style.maxHeight = content.scrollHeight + 'px';
          } else {
            content.style.maxHeight = null;
          }
        }
      });

      // Mobile filter sidebar toggle
      const filterToggle = this.container.querySelector('[data-filter-toggle]');
      const filterSidebar = this.container.querySelector('.collection-filters');
      const filterOverlay = this.container.querySelector('.collection-filters__overlay');
      const filterClose = this.container.querySelector('.collection-filters__close');

      if (filterToggle && filterSidebar) {
        filterToggle.addEventListener('click', () => {
          filterSidebar.classList.add('collection-filters--open');
          if (filterOverlay) filterOverlay.classList.add('collection-filters__overlay--visible');
          Utils.lockScroll();
        });
      }

      if (filterClose && filterSidebar) {
        filterClose.addEventListener('click', () => {
          filterSidebar.classList.remove('collection-filters--open');
          if (filterOverlay) filterOverlay.classList.remove('collection-filters__overlay--visible');
          Utils.unlockScroll();
        });
      }

      if (filterOverlay && filterSidebar) {
        filterOverlay.addEventListener('click', () => {
          filterSidebar.classList.remove('collection-filters--open');
          filterOverlay.classList.remove('collection-filters__overlay--visible');
          Utils.unlockScroll();
        });
      }
    }

    _bindSort() {
      const sortSelect = this.container.querySelector('[data-sort-select]');
      if (!sortSelect) return;

      sortSelect.addEventListener('change', () => {
        const sortValue = sortSelect.value;
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortValue);
        window.location.href = url.toString();
      });
    }
  }

  /* ==========================================================================
     Search Overlay
     ========================================================================== */

  class SearchOverlay {
    constructor() {
      this.overlay = document.querySelector('.search-overlay');
      this.toggleBtns = document.querySelectorAll('[data-search-toggle]');
      this.closeBtn = document.querySelector('.search-overlay__close');
      this.input = document.querySelector('.search-overlay__input');
      this.resultsContainer = document.querySelector('.search-overlay__results');

      if (!this.overlay) return;

      this._bindEvents();
    }

    _bindEvents() {
      this.toggleBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Close on overlay background click
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.close();
        }
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Predictive search
      if (this.input && this.resultsContainer) {
        this.input.addEventListener(
          'input',
          Utils.debounce(() => this._predictiveSearch(), 300)
        );
      }
    }

    async _predictiveSearch() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.resultsContainer.innerHTML = '';
        return;
      }

      try {
        const url = rootUrl + 'search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6';
        const data = await Utils.fetchJSON(url);

        if (data.resources && data.resources.results && data.resources.results.products) {
          const products = data.resources.results.products;
          if (products.length === 0) {
            this.resultsContainer.innerHTML = '<p class="search-overlay__no-results">No results found</p>';
            return;
          }

          let html = '<div class="search-overlay__products">';
          products.forEach((product) => {
            const imageUrl = product.featured_image
              ? product.featured_image.url + '&width=100'
              : '';
            html +=
              '<a href="' + product.url + '" class="search-overlay__product">' +
                (imageUrl
                  ? '<img src="' + imageUrl + '" alt="' + (product.title || '').replace(/"/g, '&quot;') + '" class="search-overlay__product-image" loading="lazy">'
                  : '') +
                '<div class="search-overlay__product-info">' +
                  '<p class="search-overlay__product-title">' + product.title + '</p>' +
                  '<p class="search-overlay__product-price">' + Utils.formatMoney(product.price) + '</p>' +
                '</div>' +
              '</a>';
          });
          html += '</div>';
          html += '<a href="' + rootUrl + 'search?q=' + encodeURIComponent(query) + '" class="search-overlay__view-all">' + ((window.__cartStrings && window.__cartStrings.viewAllResults) || 'View all results') + '</a>';
          this.resultsContainer.innerHTML = html;
        }
      } catch (err) {
        console.error('Predictive search error:', err);
      }
    }

    open() {
      this.overlay.classList.add('search-overlay--open');
      Utils.lockScroll();
      this.overlay.setAttribute('aria-hidden', 'false');
      if (this.input) {
        setTimeout(() => this.input.focus(), 100);
      }
    }

    close() {
      this.overlay.classList.remove('search-overlay--open');
      Utils.unlockScroll();
      this.overlay.setAttribute('aria-hidden', 'true');
      if (this.input) this.input.value = '';
      if (this.resultsContainer) this.resultsContainer.innerHTML = '';
    }

    isOpen() {
      return this.overlay.classList.contains('search-overlay--open');
    }
  }

  /* ==========================================================================
     Tab Switching (Trending Products Section)
     ========================================================================== */

  class TabSwitcher {
    constructor(el) {
      this.el = el;
      this.tabs = el.querySelectorAll('[data-tab-trigger]');
      this.panels = el.querySelectorAll('[data-tab-panel]');

      if (!this.tabs.length || !this.panels.length) return;

      this._bindEvents();
    }

    _bindEvents() {
      this.el.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-tab-trigger]');
        if (!trigger) return;

        e.preventDefault();
        const tabId = trigger.dataset.tabTrigger;
        this._activate(tabId, trigger);
      });
    }

    _activate(tabId, trigger) {
      // Deactivate all tabs
      this.tabs.forEach((tab) => {
        tab.classList.remove('tab--active');
        tab.setAttribute('aria-selected', 'false');
      });

      // Deactivate all panels
      this.panels.forEach((panel) => {
        panel.classList.remove('tab-panel--active');
        panel.setAttribute('aria-hidden', 'true');
      });

      // Activate selected tab
      if (trigger) {
        trigger.classList.add('tab--active');
        trigger.setAttribute('aria-selected', 'true');
      }

      // Activate corresponding panel
      const panel = this.el.querySelector('[data-tab-panel="' + tabId + '"]');
      if (panel) {
        panel.classList.add('tab-panel--active');
        panel.setAttribute('aria-hidden', 'false');
      }
    }
  }

  /* ==========================================================================
     Newsletter Form
     ========================================================================== */

  class NewsletterForm {
    constructor() {
      this._bindEvents();
    }

    _bindEvents() {
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('.newsletter-form');
        if (!form) return;

        // If the form has data-ajax attribute, handle via AJAX
        if (form.dataset.ajax !== undefined) {
          e.preventDefault();
          this._submitForm(form);
        }
        // Otherwise let the form submit normally (e.g., to Shopify customer API or Mailchimp)
      });
    }

    async _submitForm(form) {
      const emailInput = form.querySelector('input[type="email"]');
      const submitBtn = form.querySelector('[type="submit"]');
      const messageEl = form.querySelector('.newsletter-form__message');

      if (!emailInput || !emailInput.value.trim()) {
        if (messageEl) {
          messageEl.textContent = 'Please enter a valid email address.';
          messageEl.className = 'newsletter-form__message newsletter-form__message--error';
        }
        return;
      }

      if (submitBtn) submitBtn.disabled = true;

      try {
        const formData = new FormData(form);
        await fetch(form.action, {
          method: 'POST',
          body: formData,
        });

        if (messageEl) {
          messageEl.textContent = 'Thank you for subscribing!';
          messageEl.className = 'newsletter-form__message newsletter-form__message--success';
        }
        form.reset();
        Toast.success('Successfully subscribed!');
      } catch (err) {
        if (messageEl) {
          messageEl.textContent = 'Something went wrong. Please try again.';
          messageEl.className = 'newsletter-form__message newsletter-form__message--error';
        }
        Toast.error('Subscription failed. Please try again.');
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    }
  }

  /* ==========================================================================
     Scroll Animations (Intersection Observer)
     ========================================================================== */

  class ScrollAnimations {
    constructor() {
      this.animatedElements = document.querySelectorAll('[data-animate]');
      if (!this.animatedElements.length) return;

      if (!('IntersectionObserver' in window)) {
        // Fallback: show all elements immediately
        this.animatedElements.forEach((el) => el.classList.add('animate--visible'));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate--visible');
              this.observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px',
        }
      );

      this.animatedElements.forEach((el) => this.observer.observe(el));
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ==========================================================================
     Announcement Bar
     ========================================================================== */

  class AnnouncementBar {
    constructor() {
      this.bar = document.querySelector('.announcement-bar');
      if (!this.bar) return;

      this.closeBtn = this.bar.querySelector('.announcement-bar__close');
      this.storageKey = 'announcement-bar-dismissed';

      // Check if previously dismissed
      if (this._isDismissed()) {
        this.bar.style.display = 'none';
        return;
      }

      this._bindEvents();
    }

    _bindEvents() {
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.dismiss());
      }
    }

    dismiss() {
      this.bar.classList.add('announcement-bar--dismissed');
      this.bar.addEventListener('transitionend', () => {
        this.bar.style.display = 'none';
      }, { once: true });

      // Fallback
      setTimeout(() => {
        if (this.bar) this.bar.style.display = 'none';
      }, 500);

      // Remember dismissal
      try {
        localStorage.setItem(this.storageKey, Date.now().toString());
      } catch (e) {
        Utils.setCookie(this.storageKey, '1', 1);
      }
    }

    _isDismissed() {
      try {
        const dismissed = localStorage.getItem(this.storageKey);
        if (dismissed) {
          // Re-show after 24 hours
          const elapsed = Date.now() - parseInt(dismissed, 10);
          return elapsed < 24 * 60 * 60 * 1000;
        }
      } catch (e) {
        return Utils.getCookie(this.storageKey) === '1';
      }
      return false;
    }
  }

  /* ==========================================================================
     Wishlist (Basic)
     ========================================================================== */

  class Wishlist {
    constructor() {
      this.storageKey = 'theme-wishlist';
      this.items = this._load();
      this._bindEvents();
      this._updateButtons();
    }

    _load() {
      try {
        const data = localStorage.getItem(this.storageKey);
        return data ? JSON.parse(data) : [];
      } catch (e) {
        return [];
      }
    }

    _save() {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      } catch (e) {
        // Storage full or unavailable
      }
    }

    _bindEvents() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.product-card__wishlist');
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();
        const handle = btn.dataset.productHandle;
        if (!handle) return;

        this.toggle(handle);
        this._updateButton(btn, handle);
      });
    }

    toggle(handle) {
      const index = this.items.indexOf(handle);
      if (index === -1) {
        this.items.push(handle);
        Toast.success('Added to wishlist');
      } else {
        this.items.splice(index, 1);
        Toast.success('Removed from wishlist');
      }
      this._save();
    }

    has(handle) {
      return this.items.indexOf(handle) !== -1;
    }

    _updateButtons() {
      document.querySelectorAll('.product-card__wishlist').forEach((btn) => {
        const handle = btn.dataset.productHandle;
        if (handle) this._updateButton(btn, handle);
      });
    }

    _updateButton(btn, handle) {
      btn.classList.toggle('product-card__wishlist--active', this.has(handle));
    }
  }

  /* ==========================================================================
     Sticky Header
     ========================================================================== */

  class StickyHeader {
    constructor() {
      this.header = document.querySelector('.header');
      if (!this.header) return;

      this.lastScrollY = 0;
      this.headerHeight = this.header.offsetHeight;

      window.addEventListener('scroll', Utils.debounce(() => this._onScroll(), 10), { passive: true });
    }

    _onScroll() {
      const scrollY = window.scrollY;

      if (scrollY > this.headerHeight) {
        this.header.classList.add('header--sticky');

        if (scrollY > this.lastScrollY) {
          // Scrolling down
          this.header.classList.add('header--hidden');
        } else {
          // Scrolling up
          this.header.classList.remove('header--hidden');
        }
      } else {
        this.header.classList.remove('header--sticky', 'header--hidden');
      }

      this.lastScrollY = scrollY;
    }
  }

  /* ==========================================================================
     Initialize Everything on DOMContentLoaded
     ========================================================================== */

  function init() {
    // Hero Carousel(s)
    // Hero carousel is initialized inline in sections/hero-carousel.liquid
    // (scroll-snap based). The HeroCarousel class above is kept for legacy
    // compatibility but no longer auto-attaches to avoid double-init.
    document.querySelectorAll('.hero-carousel').forEach((el) => {
      if (el.__heroCarouselInit) return;
      new HeroCarousel(el);
    });

    // Mobile Menu
    new MobileMenu();

    // Cart Drawer
    const cartDrawer = new CartDrawer();

    // Mega Menu
    new MegaMenu();

    // Flash Sale Countdowns
    document.querySelectorAll('[data-countdown]').forEach((el) => {
      new FlashSaleCountdown(el);
    });

    // Quick Add to Cart
    new QuickAdd(cartDrawer);

    // Product Page
    new ProductPage();

    // Collection Page
    new CollectionPage();

    // Search Overlay
    new SearchOverlay();

    // Tab Switchers
    document.querySelectorAll('[data-tabs]').forEach((el) => {
      new TabSwitcher(el);
    });

    // Newsletter Form
    new NewsletterForm();

    // Scroll Animations
    new ScrollAnimations();

    // Announcement Bar
    new AnnouncementBar();

    // Wishlist
    new Wishlist();

    // Sticky Header
    new StickyHeader();

    // Initial cart count fetch
    cartDrawer.refresh();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
